'use client';
// 'use client' required: uses useState, event handlers, TanStack Query hooks

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

import { createEntityHooks } from '@/hooks/useRateMasterV2Entity';
import type { ColumnDef, EntityTableConfig } from './entityTableConfig';
import { CreateFieldInput } from './CreateFieldInput';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCell(value: unknown, format: string): string {
  if (value === null || value === undefined) return '—';
  if (format === 'currency') return Number(value).toFixed(2);
  if (format === 'boolean') return value ? 'Yes' : 'No';
  if (format === 'date') return String(value).slice(0, 10);
  return String(value);
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TableSkeletonRows({ columns, rows = 6 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <TableRow key={rowIdx} aria-hidden="true">
          {Array.from({ length: columns }).map((_, colIdx) => (
            <TableCell key={colIdx}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main generic manager
// ---------------------------------------------------------------------------

export function RateMasterV2Manager<
  TRecord extends { id: string; is_active: boolean },
  TUpdatePayload,
  TCreatePayload = never,
>({ config }: { config: EntityTableConfig<TRecord, TUpdatePayload, TCreatePayload> }) {
  const hooks = useMemo(
    () =>
      createEntityHooks<TRecord, TUpdatePayload, TCreatePayload>(config.entityKey, config.client),
    [config],
  );

  const [showInactive, setShowInactive] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TRecord | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string | boolean>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [createValues, setCreateValues] = useState<Record<string, string | boolean>>({});
  const [createSelectedIds, setCreateSelectedIds] = useState<Record<string, number | null>>({});
  const [createError, setCreateError] = useState<string | null>(null);
  const [editSelectedIds, setEditSelectedIds] = useState<Record<string, number | null>>({});

  const { data, isLoading, isError, error } = hooks.useList({
    is_active: showInactive ? undefined : true,
  });
  const { mutateAsync: createRecord, isPending: isCreatePending } = hooks.useCreate();
  const { mutateAsync: updateRecord, isPending: isUpdatePending } = hooks.useUpdate();
  const { mutateAsync: deleteRecord, isPending: isDeletePending } = hooks.useDelete();
  const { mutateAsync: importRecords, isPending: isImportPending } = hooks.useImport();

  const records = data?.items ?? [];
  const total = data?.total ?? 0;

  function openAdd() {
    const initial: Record<string, string | boolean> = {};
    for (const field of config.createFields) {
      initial[field.key] = field.type === 'boolean' ? false : '';
    }
    setCreateValues(initial);
    setCreateSelectedIds({});
    setCreateError(null);
    setIsAddOpen(true);
  }

  function handleCreateFieldChange(key: string, value: string | boolean) {
    setCreateValues((prev) => {
      const next = { ...prev, [key]: value };
      // Clear any downstream cascaded field's value + id when this field
      // changes — e.g. changing vehicle_type invalidates a previously
      // selected vehicle_make/vehicle_model/vehicle_part_name.
      for (const field of config.createFields) {
        if (field.cascadeFrom === key) {
          next[field.key] = '';
        }
      }
      return next;
    });
    setCreateSelectedIds((prev) => {
      const next = { ...prev, [key]: prev[key] };
      for (const field of config.createFields) {
        if (field.cascadeFrom === key) {
          next[field.key] = null;
        }
      }
      return next;
    });
  }

  function handleCreateSelectedIdChange(key: string, id: number | null) {
    setCreateSelectedIds((prev) => ({ ...prev, [key]: id }));
  }

  async function handleCreate() {
    setCreateError(null);
    try {
      const payload = config.buildCreatePayload(createValues);
      await createRecord(payload);
      setIsAddOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create record');
    }
  }

  function openEdit(record: TRecord) {
    const initial: Record<string, string | boolean> = {};
    const initialSelectedIds: Record<string, number | null> = {};
    for (const field of config.editFields) {
      // Fields with a recordNameKey (dropdown kinds, or a plain-text
      // dimension field like adjustments' vehicle_type) aren't keys on
      // TRecord under field.key itself — TRecord carries the id
      // (vehicle_model_id) and, once resolved by the backend's
      // name-enrichment, a display name under a different key
      // (recordNameKey, e.g. 'vehicle_model_name'). Fields without one
      // (cost/factor columns, or natural-key text like city_name) read
      // straight off field.key.
      const value = field.recordNameKey
        ? (record[field.recordNameKey as keyof TRecord & string] as unknown)
        : (record[field.key as keyof TRecord & string] as unknown);
      initial[field.key] =
        field.type === 'boolean' ? Boolean(value) : value === null || value === undefined ? '' : String(value);

      // Seed editSelectedIds for any field a sibling cascades from, so
      // that sibling's dropdown isn't disabled on open despite this
      // field already holding a valid pre-filled parent value.
      if (field.recordIdKey) {
        const idValue = record[field.recordIdKey as keyof TRecord & string] as unknown;
        initialSelectedIds[field.key] =
          typeof idValue === 'number' ? idValue : idValue == null ? null : Number(idValue);
      }
    }
    setFormValues(initial);
    setEditSelectedIds(initialSelectedIds);
    setEditingRecord(record);
  }

  function handleEditFieldChange(key: string, value: string | boolean) {
    setFormValues((prev) => {
      const next = { ...prev, [key]: value };
      // Same cascade-reset behavior as the Create form: changing a parent
      // dropdown invalidates any already-chosen downstream selection.
      for (const field of config.editFields) {
        if (field.cascadeFrom === key) {
          next[field.key] = '';
        }
      }
      return next;
    });
    setEditSelectedIds((prev) => {
      const next = { ...prev };
      for (const field of config.editFields) {
        if (field.cascadeFrom === key) {
          next[field.key] = null;
        }
      }
      return next;
    });
  }

  function handleEditSelectedIdChange(key: string, id: number | null) {
    setEditSelectedIds((prev) => ({ ...prev, [key]: id }));
  }

  async function handleSave() {
    if (!editingRecord) return;
    const payload = config.buildUpdatePayload(formValues);
    await updateRecord({ id: editingRecord.id, payload });
    setEditingRecord(null);
  }

  async function handleDeleteConfirm() {
    if (!pendingDeleteId) return;
    try {
      await deleteRecord(pendingDeleteId);
    } finally {
      setPendingDeleteId(null);
    }
  }

  async function handleImport() {
    if (!importFile) return;
    setImportError(null);
    setImportSummary(null);
    try {
      const result = await importRecords(importFile);
      setImportSummary(
        `Imported ${result.accepted_count} row(s)` +
          (result.rejected_rows.length > 0 ? `, ${result.rejected_rows.length} rejected` : ''),
      );
      setImportFile(null);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    }
  }

  const columnCount = config.columns.length + 1; // +1 for Actions column

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <Link
          href="/super-admin/new-rate-master"
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Rate Master v2.0
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{config.title}</h2>
            <p className="text-sm text-neutral-500">{config.description}</p>
          </div>
          {config.createFields.length > 0 && (
            <Button size="sm" onClick={openAdd}>
              + Add Record
            </Button>
          )}
        </div>
      </div>

      {/* Import bar */}
      <div className="flex items-center gap-3 rounded-md border border-border-primary bg-background-secondary p-3">
        <input
          type="file"
          accept=".csv,.xlsx"
          onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
          className="text-sm"
          aria-label={`Import ${config.title} from file`}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleImport}
          disabled={!importFile || isImportPending}
          loading={isImportPending}
        >
          {isImportPending ? 'Importing…' : 'Import CSV/XLSX'}
        </Button>
        {importSummary && <span className="text-sm text-success-600">{importSummary}</span>}
        {importError && <span className="text-sm text-error-600">{importError}</span>}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <Switch
          id={`${config.entityKey}-show-inactive`}
          checked={showInactive}
          onCheckedChange={setShowInactive}
          label="Show inactive"
          size="sm"
        />
        {!isLoading && (
          <p className="text-sm text-neutral-500" aria-live="polite">
            {total} record{total === 1 ? '' : 's'} found
          </p>
        )}
      </div>

      {/* Error banner */}
      {isError && (
        <div
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700"
        >
          Failed to load records: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border-primary bg-background-primary shadow-sm overflow-hidden">
        <Table aria-label={config.title}>
          <TableHeader>
            <TableRow className="bg-neutral-50 dark:bg-neutral-800">
              {config.columns.map((col: ColumnDef<TRecord>) => (
                <TableHead
                  key={col.key}
                  className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-neutral-500"
                >
                  {col.header}
                </TableHead>
              ))}
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableSkeletonRows columns={columnCount} rows={6} />}

            {!isLoading && !isError && records.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-12 text-center text-sm text-neutral-500">
                  No records found.{' '}
                  {!showInactive && (
                    <button
                      type="button"
                      onClick={() => setShowInactive(true)}
                      className="underline hover:text-neutral-700"
                    >
                      Show inactive
                    </button>
                  )}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              records.map((record) => (
                <TableRow
                  key={record.id}
                  className={!record.is_active ? 'opacity-50 hover:opacity-60' : undefined}
                >
                  {config.columns.map((col: ColumnDef<TRecord>) => (
                    <TableCell
                      key={col.key}
                      className={
                        col.format === 'currency' || col.format === 'number'
                          ? 'text-right font-mono text-xs tabular-nums'
                          : 'text-xs'
                      }
                    >
                      {col.format === 'badge' ? (
                        <Badge variant={record[col.key] ? 'success' : 'secondary'} size="sm" shape="pill" dot>
                          {formatCell(record[col.key], col.format)}
                        </Badge>
                      ) : (
                        formatCell(record[col.key], col.format)
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(record)}
                        aria-label={`Edit record ${record.id}`}
                        className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                      >
                        Edit
                      </Button>
                      {record.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDeleteId(record.id)}
                          disabled={isDeletePending}
                          aria-label={`Deactivate record ${record.id}`}
                          className="text-error-600 hover:text-error-700 hover:bg-error-50"
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      <Dialog open={editingRecord !== null} onOpenChange={(open) => !open && setEditingRecord(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {config.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {config.editFields.map((field) => (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={`field-${field.key}`}>{field.label}</Label>
                {field.type === 'boolean' ? (
                  <Switch
                    id={`field-${field.key}`}
                    checked={Boolean(formValues[field.key])}
                    onCheckedChange={(checked) =>
                      setFormValues((prev) => ({ ...prev, [field.key]: checked }))
                    }
                  />
                ) : field.type === 'number' || field.type === 'text' ? (
                  <Input
                    id={`field-${field.key}`}
                    variant={field.type === 'number' ? 'number' : 'text'}
                    step={field.step}
                    value={String(formValues[field.key] ?? '')}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                  />
                ) : (
                  <CreateFieldInput
                    field={field}
                    values={formValues}
                    onChange={handleEditFieldChange}
                    selectedIds={editSelectedIds}
                    onSelectedIdChange={handleEditSelectedIdChange}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditingRecord(null)} disabled={isUpdatePending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isUpdatePending} loading={isUpdatePending}>
              {isUpdatePending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add record dialog */}
      <Dialog open={isAddOpen} onOpenChange={(open) => !open && setIsAddOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {config.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-neutral-500">
              New records go into a PENDING version — approve it on the Version History screen
              before it affects pricing.
            </p>
            {config.createFields.map((field) => (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={`create-field-${field.key}`}>
                  {field.label}
                  {field.required && <span className="text-error-600"> *</span>}
                </Label>
                <CreateFieldInput
                  field={field}
                  values={createValues}
                  onChange={handleCreateFieldChange}
                  selectedIds={createSelectedIds}
                  onSelectedIdChange={handleCreateSelectedIdChange}
                />
              </div>
            ))}
            {createError && (
              <div
                role="alert"
                className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
              >
                {createError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsAddOpen(false)} disabled={isCreatePending}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreatePending} loading={isCreatePending}>
              {isCreatePending ? 'Adding…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <AlertDialog open={pendingDeleteId !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The record will be marked inactive and excluded from new
              calculations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDeleteId(null)} disabled={isDeletePending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeletePending}
              className="bg-error-500 text-white hover:bg-error-600 focus:ring-error-400"
            >
              {isDeletePending ? 'Deactivating…' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
